import * as Sentry from '@sentry/react';

export interface SentryAuthContext {
  userId?: string;
  email?: string;
  tenantId?: string;
}

interface PersistedAuthState {
  state?: {
    user?: {
      id?: string;
      email?: string;
      tenantId?: string;
    } | null;
  };
}

function parseJson<T>(raw: string | null): T | null {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function readStoredAuthContext(
  storage?: Pick<Storage, 'getItem'>,
): SentryAuthContext | null {
  if (!storage) {
    return null;
  }

  const persistedAuth = parseJson<PersistedAuthState>(
    storage.getItem('auth-storage'),
  );
  const directUser = parseJson<{
    id?: string;
    email?: string;
    tenantId?: string;
  }>(storage.getItem('user'));

  const user = persistedAuth?.state?.user ?? directUser;

  if (!user?.id && !user?.email && !user?.tenantId) {
    return null;
  }

  return {
    userId: user?.id,
    email: user?.email,
    tenantId: user?.tenantId,
  };
}

export function syncSentryUserContext(storage?: Pick<Storage, 'getItem'>): void {
  const authContext =
    readStoredAuthContext(
      storage ?? (typeof window !== 'undefined' ? window.localStorage : undefined),
    ) ?? null;

  if (!authContext) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: authContext.userId,
    email: authContext.email,
  });

  if (authContext.tenantId) {
    Sentry.setTag('tenant', authContext.tenantId);
  }

  if (authContext.userId) {
    Sentry.setTag('userId', authContext.userId);
  }
}
